import React from 'react';
import { motion } from 'framer-motion';
import type { NavItem } from './Navigation.types';

interface SubPanelIndicatorProps {
  /** The 3 sub-panel descriptors for the active section */
  subPanels: [NavItem, NavItem, NavItem];
  /** Active sub-panel index (0, 1, 2) */
  active: number;
  /** Called when the user taps a dot */
  onTap: (index: number) => void;
}

/**
 * Three dots shown at the top of the screen indicating which sub-panel
 * the user is on within the current section.
 *
 * Each dot is tappable (alternative to swiping). The active dot is
 * wider (pill shape) and shows the sub-panel label.
 */
export function SubPanelIndicator({ subPanels, active, onTap }: SubPanelIndicatorProps) {
  return (
    <div className="flex items-center justify-center gap-2 py-2">
      {subPanels.map((panel, index) => {
        const isActive = index === active;

        return (
          <button
            key={panel.id}
            onClick={() => onTap(index)}
            className="flex items-center justify-center outline-none focus-visible:ring-1 rounded-full"
            style={{
              minHeight: 28,
              WebkitTapHighlightColor: 'transparent',
            }}
            aria-label={panel.label}
            aria-current={isActive ? 'true' : undefined}
          >
            <motion.div
              className="flex items-center justify-center rounded-full"
              animate={{
                width: isActive ? 'auto' : 8,
                height: 8,
                opacity: isActive ? 1 : 0.35,
              }}
              transition={{ type: 'spring', damping: 22, stiffness: 260 }}
              style={{
                background: isActive
                  ? 'var(--color-accent, #06b6d4)'
                  : 'var(--text-color, #ffffff)',
                paddingLeft: isActive ? 10 : 0,
                paddingRight: isActive ? 10 : 0,
              }}
            >
              {isActive && (
                <motion.span
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-[10px] font-semibold leading-none whitespace-nowrap"
                  style={{ color: 'var(--app-bg, #000)' }}
                >
                  {panel.label}
                </motion.span>
              )}
            </motion.div>
          </button>
        );
      })}
    </div>
  );
}
